/**
 * Converts an authored label into a URL-safe hash slug.
 *
 * - Latin diacritics are stripped (`Café` → `cafe`) to keep existing slugs stable.
 *   Internally this is done by NFKD-decomposing, removing combining marks in the
 *   `\u0300-\u036f` block, then NFC-recomposing so non-Latin scripts (whose
 *   combining marks live in other blocks) are output in canonical composed form.
 *   This matters for matching: precomposed `أ` (U+0623) and decomposed
 *   `ا` + `ٔ` (U+0627 U+0654) display identically but are not byte-equal.
 * - Non-Latin scripts (Arabic, Devanagari, CJK, etc.) are preserved as Unicode so
 *   localized deep links remain unique. Callers that write the slug to the URL
 *   (e.g. `setHashSlug`) are responsible for percent-encoding via
 *   `encodeURIComponent`; readers should `decodeURIComponent` and then re-apply
 *   `toUrlSlug` to canonicalize. The function is idempotent under that
 *   round-trip.
 * - Whitespace and ASCII punctuation collapse to a single `-`. Leading and
 *   trailing separators are trimmed.
 * - Invisible formatting characters (control chars, BOM, bidi marks/embeddings,
 *   word joiner, zero-width space) are stripped before slug generation so two
 *   labels that look identical produce identical slugs. ZWNJ (U+200C) and ZWJ
 *   (U+200D) are deliberately preserved because they carry semantic meaning in
 *   Persian, Indic conjuncts, and emoji ZWJ sequences — stripping them would
 *   change which word the slug represents.
 */
const INVISIBLE_FORMATTING_CHARACTERS_REGEX = /[\u200b\u200e\u200f\u202a-\u202e\u2060-\u206f\ufeff]/g;
const DELETE_CONTROL_CHARACTER_CODE_POINT = 0x7f;
const LAST_C0_CONTROL_CHARACTER_CODE_POINT = 0x1f;

function stripControlCharacters(value: string): string {
  return Array.from(value)
    .filter(character => {
      const codePoint = character.codePointAt(0);
      return (
        codePoint === undefined ||
        (codePoint > LAST_C0_CONTROL_CHARACTER_CODE_POINT && codePoint !== DELETE_CONTROL_CHARACTER_CODE_POINT)
      );
    })
    .join('');
}

export function toUrlSlug(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return '';
  return stripControlCharacters(trimmed)
    .replace(INVISIBLE_FORMATTING_CHARACTERS_REGEX, '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s!"#$%&'()*+,./:;<=>?@[\]\\^`{|}~_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
